import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import withRouter from "Components/Common/withRouter"
import FormPageTemplate from "Components/templates/form-page-template"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import DepartmentForm from "Components/organisms/forms/department-form"
import DepartmentFields from "Components/organisms/forms/department-form/types"
import { DepartmentFormValidation } from "data/department-form"
import useAreYouSureModal from "hooks/useAreYouSureModal"
import { DepartmentLevel } from "models/department-level"
import Department from "models/department"
import Employee from "models/employee"
import { PermissionTypeName } from "models/employee-department-permission"
import { getDepartment, getDepartmentFormFields, submitEditDepartmentForm } from "slices/admin/departments/thunk"
import { navigateToDepartmentDetails } from "utils/navigator"

const EditDepartmentPage = (props: any) => {
    document.title = "Edit Department | IbexIQ";
    const [searchParams] = useSearchParams()
    const departmentId = searchParams.get("departmentId")

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [department, setDepartment] = useState<Department | null>(null)
    const [departmentLevels, setDepartmentLevels] = useState<DepartmentLevel[]>([])
    const [departments, setDepartments] = useState<Department[]>([])
    const [employees, setEmployees] = useState<Employee[]>([])

    const confirmModal = useAreYouSureModal()

    const initializePage = async () => {
        if (!departmentId) {
            toast.error("Department ID not found")
            props.router.navigate(-1)
            return
        }

        const [dept, fields] = await Promise.all([
            getDepartment(departmentId),
            getDepartmentFormFields()
        ])

        if (!dept) {
            toast.error("Could not find department")
            props.router.navigate(-1)
            return
        }

        // Mirrors the Admin+ check the API enforces on PATCH /departments/:id.
        const canManage = !!dept.isGlobalAdmin
            || dept.yourPermission === PermissionTypeName.Owner
            || dept.yourPermission === PermissionTypeName.Admin

        if (!canManage) {
            toast.error("You do not have permission to edit this department")
            props.router.navigate(-1)
            return
        }

        setDepartment(dept)
        if (fields) {
            setDepartmentLevels(fields.departmentLevels)
            setDepartments(fields.departments.filter(d => d.id !== departmentId))
            setEmployees(fields.employees)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [departmentId])

    const handleBack = () => {
        if (!departmentId) return
        navigateToDepartmentDetails(props.router.navigate, departmentId)
    }

    const onSubmit = async (vals: DepartmentFields) => {
        if (!departmentId) return

        confirmModal.open({
            title: "Update Department",
            subTitle: `Save these changes to ${department?.code}?`,
            acceptText: "Update",
            processingText: "Saving...",
            action: async () => {
                const res = await submitEditDepartmentForm(departmentId, vals)
                if (res) {
                    navigateToDepartmentDetails(props.router.navigate, departmentId)
                }
                return res
            }
        })
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isValid,
        setFieldValue,
        isSubmitting
    } = useFormik({
        initialValues: {
            code: department?.code ?? '',
            description: department?.description ?? '',
            departmentLevelId: department?.levelId ?? '',
            parentDepartmentId: department?.parentId ?? '',
            departmentHeadId: department?.headId ?? '',
            color: department?.color ?? '',
            isBillable: department?.isBillable ?? true
        } as DepartmentFields,
        onSubmit,
        enableReinitialize: true,
        validationSchema: DepartmentFormValidation
    })

    const departmentLevelOptions = useMemo(() => departmentLevels.map(l => ({
        value: l.id, label: l.description
    })), [departmentLevels])

    const parentDepartmentOptions = useMemo(() => departments.map(d => ({
        value: d.id, label: `${d.code} — ${d.description}`
    })), [departments])

    const employeeOptions = useMemo(() => employees.map(e => ({
        value: e.id, label: e.commonName ?? `${e.firstName} ${e.lastName}`
    })), [employees])

    return <FormPageTemplate
        title="Admin"
        breadcrumbItem="Edit Department"
        isLoading={isLoading}
        loadingTitle="Loading Department"
        loadingSubtitle="Fetching department details..."
        form={<DepartmentForm
            edit
            departmentLevels={departmentLevelOptions}
            parentDepartments={parentDepartmentOptions}
            employees={employeeOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
        />}
        modal={<AreYouSureModal {...confirmModal.modalProps} />}
    />
}

export default withRouter(EditDepartmentPage)
